import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthHero from '../../../shared/auth/components/AuthHero';
import FlowGuard from '../../../shared/components/FlowGuard';
import { CANDIDATE_ROUTES } from '../../constants';

/**
 * SignUp Success Content
 * Displays confirmation message after successful candidate signup
 */
const SignUpSuccessContent: React.FC = () => {
    const navigate = useNavigate();

    // Automatically redirect to welcome page after 3 seconds
    useEffect(() => {
        const timer = setTimeout(() => {
            navigate(CANDIDATE_ROUTES.WELCOME, { replace: true });
        }, 3000);

        return () => clearTimeout(timer);
    }, [navigate]);

    return (
        <div className="min-h-screen bg-[#F8F9FB] flex" style={{ fontFamily: 'Manrope' }}>
            {/* Left Side - Confirmation Message */}
            <div className="flex-1 flex items-center justify-center p-12 relative">
                <div className="w-full max-w-[700px] flex flex-col items-center">
                    {/* Logo */}
                    <div className="absolute top-[76px] left-[212px]">
                        <img
                            src="/branding/logo_blue.svg"
                            alt="Hush"
                            className="h-12 w-auto"
                        />
                    </div>

                    {/* Main Content */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        maxWidth: '491px'
                    }}>
                        {/* Main Heading */}
                        <h1
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 700,
                                fontSize: '48px',
                                lineHeight: '100%',
                                textAlign: 'center',
                                color: '#015FA6',
                                marginBottom: '48px',
                                maxWidth: '445px'
                            }}
                        >
                            We look forward to working with you
                        </h1>

                        {/* Subtext */}
                        <p
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 200,
                                fontSize: '24px',
                                lineHeight: '150%',
                                textAlign: 'center',
                                color: '#015FA6',
                                maxWidth: '491px',
                                marginBottom: '24px'
                            }}
                        >
                            We look forward to working with you, our team will be in touch shortly.
                        </p>

                        {/* Redirect Message */}
                        <p
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 400,
                                fontSize: '16px',
                                lineHeight: '150%',
                                textAlign: 'center',
                                color: '#64748B',
                                maxWidth: '491px'
                            }}
                        >
                            Redirecting you to complete your profile setup...
                        </p>
                    </div>

                    {/* Checkbox - appears to be a decorative element based on the design */}
                    <div style={{
                        position: 'absolute',
                        bottom: 'calc(50% - 199px)',
                        left: 'calc(50% - 733px)',
                        width: '16px',
                        height: '16px',
                        border: '1px solid #CFD9E0',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                    </div>

                    {/* Navigation Dots */}
                    <div style={{
                        position: 'absolute',
                        bottom: '48px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'center'
                    }}>
                        <div style={{
                            width: '22px',
                            height: '22px',
                            border: '2px solid #FFFFFF',
                            borderRadius: '50%',
                            background: 'transparent',
                            transform: 'rotate(-34.85deg)'
                        }}></div>
                        <div style={{
                            width: '4px',
                            height: '4px',
                            background: 'rgba(237, 242, 247, 0.3)',
                            border: '1px solid #FFFFFF',
                            borderRadius: '50%'
                        }}></div>
                        <div style={{
                            width: '7px',
                            height: '8px',
                            background: 'rgba(237, 242, 247, 0.3)',
                            border: '1px solid #FFFFFF'
                        }}></div>
                        <div style={{
                            width: '8px',
                            height: '8px',
                            background: '#D9D9D9',
                            border: '1px solid #FFFFFF',
                            borderRadius: '50%'
                        }}></div>
                        <div style={{
                            width: '7px',
                            height: '8px',
                            background: '#CBD5E0',
                            border: '1px solid #FFFFFF'
                        }}></div>
                        <div style={{
                            width: '4px',
                            height: '4px',
                            background: 'rgba(237, 242, 247, 0.3)',
                            border: '1px solid #FFFFFF',
                            borderRadius: '50%'
                        }}></div>
                    </div>
                </div>
            </div>

            {/* Right Side - Hero */}
            <div className="flex-1">
                <AuthHero />
            </div>
        </div>
    );
};

/**
 * SignUpSuccess Page
 * Protected by FlowGuard - only accessible after candidate signup flow
 * Direct URL access redirects to candidate signup page
 * Route: /auth/candidate/signup-success
 */
const SignUpSuccess: React.FC = () => {
    return (
        <FlowGuard
            requiredStateKey="fromCandidateSignup"
            fallbackPath="/auth/candidate/signup"
        >
            <SignUpSuccessContent />
        </FlowGuard>
    );
};

export default SignUpSuccess;
